import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config.js';
import { sourceOf, sourceParam } from '../constants/visitsSource.js';
import { DEFAULT_VISITS_CONFIG } from '../constants/visitsConfig.js';

/**
 * Per-client Visits options for the selected (source, project) pair.
 * Never throws: any error resolves to the default (open) config, and a
 * response for a previously selected project is dropped.
 */
const useVisitsConfig = ({ selectedProject, fetchWithAuth }) => {
  // Tagged with its project: `loaded` is derived, a previous project's options never leak.
  const [state, setState] = useState({ key: null, config: DEFAULT_VISITS_CONFIG });
  const projectId = selectedProject?.value;
  const source = sourceOf(selectedProject);
  const hasProject = projectId !== undefined && projectId !== null;
  const key = hasProject ? `${source}:${projectId}` : null;

  useEffect(() => {
    if (key === null) return undefined;
    let cancelled = false;

    const fetchConfig = async () => {
      let config = DEFAULT_VISITS_CONFIG;
      try {
        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/visits/${projectId}/config?${sourceParam({ sourceSystem: source })}`
        );
        // A malformed payload is safe: getFeatureAccess reads it as open.
        if (response?.ok) config = (await response.json()) || DEFAULT_VISITS_CONFIG;
      } catch (error) {
        console.error('Error fetching visits config:', error);
      }
      if (!cancelled) setState({ key, config });
    };

    fetchConfig();
    return () => { cancelled = true; };
  }, [key, projectId, source, fetchWithAuth]);

  if (!hasProject) return { config: DEFAULT_VISITS_CONFIG, loaded: true };
  return state.key === key
    ? { config: state.config, loaded: true }
    : { config: DEFAULT_VISITS_CONFIG, loaded: false };
};

export default useVisitsConfig;
